import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import {
  useMembers,
  useUpdateMemberRole,
  useRemoveMember,
  useInvitations,
  useCancelInvitation,
  useResendInvitation,
} from '../../hooks/useOrganizations';
import { useCurrentOrganization } from '../../contexts/OrganizationContext';
import { InviteMemberModal } from '../../components/organizations/InviteMemberModal';
import { ProjectRolesModal } from '../../components/organizations/ProjectRolesModal';
import type { Invitation, Member, OrganizationRole } from '../../types/organization';
import { ROLE_HIERARCHY } from '../../types/organization';
import { useAuth } from '../../contexts/AuthContext';
import {
  Badge,
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type BadgeTone,
} from '../../ui';
import './MembersPage.css';

const ROLE_OPTIONS: OrganizationRole[] = ['viewer', 'member', 'admin', 'owner'];

const roleTones: Record<OrganizationRole, BadgeTone> = {
  owner: 'info',
  admin: 'info',
  member: 'success',
  viewer: 'neutral',
};

export function MembersPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { user } = useAuth();
  const { currentRole, currentOrgId, isOwner, canManageMembers, setCurrentOrgId } =
    useCurrentOrganization();

  useEffect(() => {
    if (orgId && orgId !== currentOrgId) {
      setCurrentOrgId(orgId);
    }
  }, [orgId, currentOrgId, setCurrentOrgId]);

  const { data: members, isLoading, error } = useMembers(orgId);
  const { data: invitations } = useInvitations(canManageMembers ? orgId : undefined);
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const cancelInvitation = useCancelInvitation();
  const resendInvitation = useResendInvitation();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<Member | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<Invitation | null>(null);
  const [projectRolesMember, setProjectRolesMember] = useState<Member | null>(null);
  const [mfaFilterOnly, setMfaFilterOnly] = useState(false);

  const mfaCounts = useMemo(() => {
    const list = members ?? [];
    return list.reduce(
      (acc, m) => {
        if (m.mfa_required) acc.required += 1;
        if (m.mfa_required && !m.mfa_enabled) acc.missing += 1;
        if (m.mfa_setup_overdue) acc.overdue += 1;
        return acc;
      },
      { required: 0, missing: 0, overdue: 0 }
    );
  }, [members]);

  const visibleMembers = useMemo(() => {
    const list = members ?? [];
    if (!mfaFilterOnly) return list;
    return list.filter(m => m.mfa_required && !m.mfa_enabled);
  }, [members, mfaFilterOnly]);

  const canChangeRole = (targetRole: OrganizationRole): boolean => {
    if (!currentRole) return false;
    return isOwner && ROLE_HIERARCHY[targetRole] < ROLE_HIERARCHY[currentRole];
  };

  const canRemoveMember = (targetRole: OrganizationRole, targetId: string): boolean => {
    if (!currentRole || !user) return false;
    if (targetId === String(user.id)) return false;
    return ROLE_HIERARCHY[currentRole] > ROLE_HIERARCHY[targetRole];
  };

  const handleRoleChange = async (userId: string, newRole: OrganizationRole) => {
    if (!orgId) return;
    try {
      await updateRole.mutateAsync({ orgId, userId, role: newRole });
    } catch {
      /* handled by mutation */
    }
  };

  const handleRemoveMember = async (member: Member) => {
    if (!orgId) return;
    try {
      await removeMember.mutateAsync({ orgId, userId: member.id });
      setConfirmRemove(null);
    } catch {
      /* handled by mutation */
    }
  };

  const handleCancelInvitation = async (invitation: Invitation) => {
    if (!orgId) return;
    try {
      await cancelInvitation.mutateAsync({ orgId, invitationId: invitation.id });
      setConfirmCancel(null);
    } catch {
      /* handled by mutation */
    }
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    if (!orgId) return;
    try {
      await resendInvitation.mutateAsync({ orgId, invitationId: invitation.id });
    } catch {
      /* handled by mutation */
    }
  };

  if (isLoading) {
    return (
      <div className="members-page">
        <div className="members-page__loading">
          <div className="spinner" />
          <p>Loading members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="members-page">
        <div className="members-page__error">
          <p>Failed to load members</p>
          <p className="members-page__error-detail">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="members-page">
      <header className="members-page__header">
        <div>
          <h1>Members</h1>
          <p className="members-page__subtitle">
            People and pending invitations for this organization.
          </p>
        </div>
        {canManageMembers && (
          <Button onClick={() => setShowInviteModal(true)}>+ Invite Member</Button>
        )}
      </header>

      {mfaCounts.required > 0 && (
        <div
          className={`members-page__mfa-banner${
            mfaCounts.overdue > 0 ? ' members-page__mfa-banner--alert' : ''
          }`}
          role="status"
        >
          <ShieldAlert size={18} aria-hidden="true" />
          <div className="members-page__mfa-banner-text">
            <strong>
              {mfaCounts.missing === 0
                ? 'All required members have enrolled in MFA.'
                : `${mfaCounts.missing} of ${mfaCounts.required} required ${
                    mfaCounts.required === 1 ? 'member is' : 'members are'
                  } missing MFA`}
            </strong>
            {mfaCounts.overdue > 0 && (
              <span className="members-page__mfa-banner-detail">
                {' '}
                {mfaCounts.overdue} past the grace period and blocked from logging in.
              </span>
            )}
          </div>
          {mfaCounts.missing > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setMfaFilterOnly(v => !v)}
              aria-pressed={mfaFilterOnly}
            >
              {mfaFilterOnly ? 'Show all' : 'View missing'}
            </Button>
          )}
        </div>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Member</TableHeaderCell>
            <TableHeaderCell>Role</TableHeaderCell>
            <TableHeaderCell>MFA</TableHeaderCell>
            <TableHeaderCell>Project access</TableHeaderCell>
            <TableHeaderCell>Joined</TableHeaderCell>
            {canManageMembers && (
              <TableHeaderCell className="members-page__actions-col">Actions</TableHeaderCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleMembers.map(member => (
            <TableRow key={member.id}>
              <TableCell>
                <div className="members-page__member">
                  <div className="members-page__avatar">
                    {member.avatar_url ? (
                      <img src={member.avatar_url} alt={member.name} />
                    ) : (
                      <span>{member.name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="members-page__member-text">
                    <span className="members-page__member-name">
                      {member.name}
                      {String(member.id) === String(user?.id) && (
                        <span className="members-page__you-badge">You</span>
                      )}
                    </span>
                    <span className="members-page__member-email">{member.email}</span>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                {isOwner && canChangeRole(member.role) ? (
                  <select
                    value={member.role}
                    onChange={e => handleRoleChange(member.id, e.target.value as OrganizationRole)}
                    className="members-page__role-select"
                    disabled={updateRole.isPending}
                    aria-label={`Change role for ${member.name}`}
                  >
                    {ROLE_OPTIONS.map(role => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Badge tone={roleTones[member.role]}>{member.role}</Badge>
                )}
              </TableCell>
              <TableCell>
                <MfaStatusCell member={member} />
              </TableCell>
              <TableCell>
                <ProjectAccessSummary member={member} />
              </TableCell>
              <TableCell>
                <span className="members-page__date">
                  {new Date(member.joined_at).toLocaleDateString()}
                </span>
              </TableCell>
              {canManageMembers && (
                <TableCell className="members-page__actions-col">
                  <div className="members-page__invitation-actions">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setProjectRolesMember(member)}
                    >
                      Projects
                    </Button>
                    {canRemoveMember(member.role, member.id) && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setConfirmRemove(member)}
                        disabled={removeMember.isPending}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {canManageMembers && (
        <>
          <h2 className="members-page__section-header">Pending Invitations</h2>
          {invitations && invitations.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell>Invited By</TableHeaderCell>
                  <TableHeaderCell>Expires</TableHeaderCell>
                  <TableHeaderCell className="members-page__actions-col">Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map(invitation => (
                  <TableRow key={invitation.id}>
                    <TableCell>
                      <div className="members-page__member-text">
                        <span className="members-page__member-name">{invitation.email}</span>
                        <span className="members-page__member-email">
                          <Badge tone="warning">Pending</Badge>
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge tone={roleTones[invitation.role]}>{invitation.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="members-page__date">{invitation.invited_by.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="members-page__date">
                        {new Date(invitation.expires_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell className="members-page__actions-col">
                      <div className="members-page__invitation-actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleResendInvitation(invitation)}
                          disabled={resendInvitation.isPending}
                        >
                          Resend
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setConfirmCancel(invitation)}
                          disabled={cancelInvitation.isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              title="No pending invitations."
              description="Anyone you invite will appear here while waiting to accept."
            />
          )}
        </>
      )}

      {showInviteModal && orgId && (
        <InviteMemberModal
          orgId={orgId}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => setShowInviteModal(false)}
        />
      )}

      {projectRolesMember && orgId && (
        <ProjectRolesModal
          orgId={orgId}
          member={projectRolesMember}
          onClose={() => setProjectRolesMember(null)}
        />
      )}

      {confirmCancel && (
        <div className="modal-overlay" onClick={() => setConfirmCancel(null)}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()}>
            <header className="modal__header">
              <h2>Cancel Invitation</h2>
            </header>
            <div className="modal__body">
              <p>
                Cancel the invitation for <strong>{confirmCancel.email}</strong>?
              </p>
            </div>
            <footer className="modal__footer">
              <Button variant="secondary" onClick={() => setConfirmCancel(null)}>
                Keep
              </Button>
              <Button
                variant="danger"
                onClick={() => handleCancelInvitation(confirmCancel)}
                loading={cancelInvitation.isPending}
              >
                Cancel Invitation
              </Button>
            </footer>
          </div>
        </div>
      )}

      {confirmRemove && (
        <div className="modal-overlay" onClick={() => setConfirmRemove(null)}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()}>
            <header className="modal__header">
              <h2>Remove Member</h2>
            </header>
            <div className="modal__body">
              <p>
                Are you sure you want to remove <strong>{confirmRemove.name}</strong> from this
                organization?
              </p>
            </div>
            <footer className="modal__footer">
              <Button variant="secondary" onClick={() => setConfirmRemove(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleRemoveMember(confirmRemove)}
                loading={removeMember.isPending}
              >
                Remove
              </Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

function MfaStatusCell({ member }: { member: Member }) {
  if (member.mfa_enabled) {
    return <Badge tone="success">Enrolled</Badge>;
  }
  if (member.mfa_setup_overdue) {
    return <Badge tone="danger">Overdue</Badge>;
  }
  if (member.mfa_required) {
    return (
      <span className="members-page__mfa-cell">
        <Badge tone="warning">Required</Badge>
        {member.mfa_deadline && (
          <span className="members-page__mfa-deadline">
            by {new Date(member.mfa_deadline).toLocaleDateString()}
          </span>
        )}
      </span>
    );
  }
  return <Badge tone="neutral">Not set up</Badge>;
}

function ProjectAccessSummary({ member }: { member: Member }) {
  const projectRoles = member.project_roles ?? [];
  const allProjectsRole = projectRoles.find(r => r.scope === 'all_projects');
  const specificCount = projectRoles.filter(r => r.scope === 'project').length;

  if (projectRoles.length === 0) {
    return <span className="members-page__date">No project access</span>;
  }

  return (
    <div className="members-page__project-access">
      {allProjectsRole && <Badge tone="info">All projects · {allProjectsRole.role}</Badge>}
      {specificCount > 0 && (
        <Badge tone="neutral">
          {specificCount} project override{specificCount === 1 ? '' : 's'}
        </Badge>
      )}
    </div>
  );
}
